import { createElement, addEventListeners } from "../utils/dom.js";
import { createIcon } from "./icons.js";
import { EMOJI_OPTIONS } from "../config/index.js";

/**
 * Create modal header
 * @param {Object} config - Configuration
 * @param {Function} onClose - Close callback
 * @returns {HTMLElement} Header element
 */
export function createModalHeader(config, onClose) {
    const header = createElement("div", "feedback-header");

    const title = createElement("h3", "", {
        innerHTML: config.headerText,
        id: "feedback-header",
    });

    const closeButton = createElement("button", "close-button", {
        "aria-label": "Close feedback form",
        innerHTML: createIcon("close", 14),
    });

    addEventListeners(closeButton, {
        click: onClose,
    });

    header.appendChild(title);
    header.appendChild(closeButton);

    return header;
}

/**
 * Create emoji selection section
 * @param {Object} config - Configuration
 * @param {Function} onEmojiSelect - Emoji selection callback
 * @returns {HTMLElement} Emoji section element
 */
export function createEmojiSection(config, onEmojiSelect) {
    const container = createElement("div", "feedback-emojis");

    EMOJI_OPTIONS.forEach(({ emoji, label }) => {
        const button = createElement("button", "emoji-button", {
            "data-emoji": emoji,
        });

        button.innerHTML = `
      <span class="emoji-icon">${emoji}</span>
      <span class="emoji-label">${label}</span>
    `;

        addEventListeners(button, {
            click: () => onEmojiSelect(emoji, button),
        });

        container.appendChild(button);
    });

    return container;
}

/**
 * Create text input section
 * @param {Object} config - Configuration
 * @param {Function} onTextChange - Text change callback
 * @returns {Object} Text section elements
 */
export function createTextSection(config, onTextChange) {
    const section = createElement("div", "feedback-text-section");

    const label = createElement("label", "", {
        innerHTML: `${config.feedbackLabelText} <span class="optional-text">(optional)</span>`,
    });

    const textarea = createElement("textarea", "feedback-textarea", {
        placeholder: config.placeholder,
        maxlength: "500",
    });

    const errorDiv = createElement("div", "error-message");

    addEventListeners(textarea, {
        input: (e) => onTextChange(e.target.value),
    });

    section.appendChild(label);
    section.appendChild(textarea);
    section.appendChild(errorDiv);

    return { section, textarea, errorDiv };
}

/**
 * Create submit button
 * @param {Object} config - Configuration
 * @param {Function} onSubmit - Submit callback
 * @returns {Object} Submit button elements
 */
export function createSubmitButton(config, onSubmit) {
    const container = createElement("div", "feedback-actions");

    const button = createElement("button", "submit-button", {
        type: "button",
        innerHTML: `<span class="button-text">${config.submitText}</span>`,
    });

    addEventListeners(button, {
        click: onSubmit,
    });

    container.appendChild(button);

    return { container, button };
}

/**
 * Create success content
 * @param {Object} config - Configuration
 * @returns {HTMLElement} Success content element
 */
export function createSuccessContent(config) {
    return createElement("div", "feedback-success success-fade-in", {
        innerHTML: `
            <div class="success-icon success-icon-animate" style="color: ${
                config.primaryColor
            }">
                ${createIcon("success", 32)}
            </div>
            <h4 class="success-title-animate">Thank you for your feedback!</h4>
            <p class="success-text-animate">We appreciate your input and will use it to improve our service.</p>
        `,
        "aria-live": "polite",
    });
}

/**
 * Create powered by footer
 * @returns {HTMLElement} Powered by element
 */
export function createPoweredBy() {
    return createElement("div", "powered-by", {
        innerHTML: `
      <a href="https://feedaura.in" target="_blank" rel="noopener noreferrer" 
         style="display: inline-flex; align-items: center; gap: 4px; text-decoration: none; color:inherit;">
        <svg width="18" height="18" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M31.5 7.23333C30.3333 7.23333 29.1667 7 28 7C26.8333 7 25.6667 7.23333 24.5 7.23333" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M45.0333 15.8666C43.6416 14.0083 41.9916 12.3583 40.1333 10.9666" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M48.7666 31.5C48.9999 30.3333 48.9999 29.1667 48.9999 28C48.9999 26.8333 48.7666 25.6667 48.7666 24.5" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M40.1333 45.0333C41.9916 43.6416 43.6416 41.9916 45.0333 40.1333" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M24.5 48.7666C25.6667 48.9999 26.8333 48.9999 28 48.9999C29.1667 48.9999 30.3333 48.7666 31.5 48.7666" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M8.1665 40.8333L4.6665 51.3333L15.1665 47.8333" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M7.23333 24.5C7.23333 25.6667 7 26.8333 7 28C7 29.1667 7.23333 30.3333 7.23333 31.5" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M15.8668 10.9666C14.0085 12.3583 12.3585 14.0083 10.9668 15.8666" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M39.3133 28.0001H36.5076C36.0132 27.9991 35.532 28.16 35.1377 28.4583C34.7434 28.7566 34.4577 29.1758 34.3242 29.6519L31.6656 39.1096C31.6485 39.1684 31.6127 39.22 31.5638 39.2567C31.5148 39.2934 31.4553 39.3133 31.3941 39.3133C31.3329 39.3133 31.2733 39.2934 31.2244 39.2567C31.1754 39.22 31.1397 39.1684 31.1226 39.1096L24.8777 16.8906C24.8606 16.8319 24.8249 16.7803 24.7759 16.7436C24.7269 16.7069 24.6674 16.687 24.6062 16.687C24.545 16.687 24.4855 16.7069 24.4365 16.7436C24.3876 16.7803 24.3518 16.8319 24.3347 16.8906L21.6761 26.3484C21.5431 26.8226 21.2591 27.2405 20.8671 27.5386C20.475 27.8367 19.9965 27.9987 19.504 28.0001H16.687" stroke="#fb2c36" stroke-width="3.05455" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
        <span>Powered by</span>
        <span class="brand-highlight">Feedaura</span>
      </a>
    `,
    });
}
